import React, { useState, useRef, useEffect } from "react";
import {
  Bot,
  Send,
  Sparkles,
  User,
  TrendingUp,
  Users,
  CreditCard,
  Ticket,
  DoorOpen,
  Wrench,
  RotateCcw,
  ChevronRight,
  Zap,
  AlertTriangle,
} from "lucide-react";
import PageLayout from "../../components/PageLayout";
import { useAuth } from "../../context/AuthContext";
import {
  analytics,
  visitors as visitorsApi,
  helpdesk,
  type AnalyticsOverview,
} from "../../services/api";

/* ── Types ── */
interface Message {
  id: string;
  role: "user" | "bot";
  text: string;
  timestamp: Date;
  chips?: string[];
}

interface QuickPrompt {
  icon: React.ReactNode;
  label: string;
  prompt: string;
  color: string;
}

/* ── Mock AI response engine ── */
function generateResponse(
  prompt: string,
  overview: AnalyticsOverview | null,
  societyName: string
): { text: string; chips?: string[] } {
  const q = prompt.toLowerCase();

  if (q.match(/due|bill|payment|collect|pending|arrear|fee/)) {
    const rate = overview ? `${(overview.collectionRate ?? 0).toFixed(1)}%` : "94%";
    const collected = overview
      ? `₹${((overview.duesCollected ?? 0) / 100000).toFixed(1)}L`
      : "₹4.2L";
    return {
      text: `Here's the current dues status for **${societyName}**:\n\n• **Collection rate:** ${rate} this month\n• **Total collected:** ${collected}\n• **Pending units:** ${overview ? Math.round((1 - (overview.collectionRate ?? 0) / 100) * overview.totalUnits) : 74} units with outstanding dues\n\nBased on payment patterns, I predict **97% collection** by month-end. I'd recommend sending automated reminders to units with dues older than 15 days.`,
      chips: ["Show overdue units", "Send bulk reminder", "Generate dues report"],
    };
  }

  if (q.match(/visitor|gate|entry|security|guard|access/)) {
    const today = overview ? overview.visitorsToday : 86;
    const pending = overview ? overview.pendingVisitors : 4;
    return {
      text: `**Visitor activity summary:**\n\n• **Today's entries:** ${today} visitors logged\n• **Pending approvals:** ${pending} awaiting resident confirmation\n• **Flagged entries:** 3 unverified in the last hour\n\n⚠️ Gate B traffic is running **40% above Tuesday average**. I recommend alerting the security team for increased monitoring between 5–8 PM.`,
      chips: ["View flagged entries", "Pending approvals", "Visitor trends"],
    };
  }

  if (q.match(/ticket|complaint|issue|helpdesk|problem|report/)) {
    const open = overview ? overview.openTickets : 23;
    const resolved = overview ? overview.resolvedToday : 5;
    return {
      text: `**Helpdesk overview:**\n\n• **Open tickets:** ${open} active\n• **Resolved today:** ${resolved}\n• **Avg. resolution time:** 18 hours\n• **Urgent tickets:** 2 require immediate attention\n\n🔧 Top issue this week: **Lift B** (3 tickets). Predictive analysis suggests scheduling preventive maintenance within 14 days to avoid breakdown.`,
      chips: ["View urgent tickets", "Maintenance schedule", "Ticket analytics"],
    };
  }

  if (q.match(/resident|unit|flat|tenant|owner|member|people/)) {
    const total = overview ? overview.totalUnits : 1248;
    const occupied = overview ? overview.occupiedUnits : 1196;
    const vacant = total - occupied;
    return {
      text: `**Community overview for ${societyName}:**\n\n• **Total units:** ${total.toLocaleString()}\n• **Occupied:** ${occupied.toLocaleString()} (${((occupied / total) * 100).toFixed(1)}%)\n• **Vacant:** ${vacant} units\n• **Active residents:** ${(occupied * 1.6).toFixed(0)} approx.\n\nOccupancy is healthy. ${vacant} vacant units represent a potential revenue recovery of ₹${(vacant * 2500).toLocaleString()}/month in maintenance fees.`,
      chips: ["View residents list", "Vacant units", "New move-ins"],
    };
  }

  if (q.match(/maintenance|repair|facility|equipment|service|lift|pump/)) {
    return {
      text: `**Predictive maintenance alerts:**\n\n• 🔴 **Lift B** — High usage variance detected. Servicing recommended within **14 days**\n• 🟡 **Water pump (Block C)** — Motor temperature readings elevated. Schedule inspection\n• 🟢 **CCTV System** — All 24 cameras operational\n• 🟢 **Generator** — Last serviced 12 days ago, next due in 18 days\n\nScheduling preventive maintenance now could save ~₹45,000 in emergency repair costs.`,
      chips: ["Schedule maintenance", "View all equipment", "Maintenance log"],
    };
  }

  if (q.match(/report|analytics|insight|trend|statistic|data|summary/)) {
    return {
      text: `**Monthly intelligence summary for ${societyName}:**\n\n📈 **Dues collection:** Up 3.2% vs last month\n👥 **Visitor traffic:** 1,842 entries this month (+12%)\n🎫 **Ticket resolution:** 94% resolved within SLA\n💰 **Expenses:** ₹3.16L spent (Security 38%, Utilities 38%)\n\n**AI Forecast for next month:**\n• Dues collection: ₹4.6L (97% confidence)\n• Expected visitors: ~2,100\n• Maintenance budget: ₹85K recommended`,
      chips: ["Download report", "View AI Insights", "Share with committee"],
    };
  }

  if (q.match(/hello|hi|hey|good|morning|afternoon|evening|help|what can/)) {
    return {
      text: `Hello! I'm **NexaBot**, your AI assistant for ${societyName}. 👋\n\nI can help you with:\n• **Dues & billing** — collection status, reminders, forecasts\n• **Visitor management** — traffic analysis, security alerts\n• **Helpdesk tickets** — summaries, priority routing, trends\n• **Residents & units** — occupancy, move-in/out tracking\n• **Maintenance** — predictive alerts, scheduling\n• **Reports** — monthly summaries, AI-powered forecasts\n\nWhat would you like to know today?`,
      chips: ["Dues status", "Visitor activity", "Open tickets", "Monthly summary"],
    };
  }

  if (q.match(/expense|cost|spend|budget|accounting|finance/)) {
    return {
      text: `**Expense analysis for this month:**\n\n• 🔵 **Security:** ₹1.2L (38%) — on budget\n• 🟢 **Electricity:** ₹68K (22%) — 4% below target\n• 🟣 **Water:** ₹52K (16%) — within range\n• 🟡 **Housekeeping:** ₹38K (12%) — on budget\n• ⚪ **Others:** ₹38K (12%)\n\n**Total:** ₹3.16L | **Budget:** ₹3.30L | **Savings:** ₹14K\n\nAI recommendation: Renegotiate the security contract at renewal — similar societies are averaging 12% lower costs.`,
      chips: ["Full expense report", "Compare with last month", "Budget planner"],
    };
  }

  return {
    text: `I understand you're asking about **"${prompt}"**. Let me give you a quick overview of ${societyName}:\n\n• Everything looks healthy across dues, visitors, and maintenance\n• ${overview ? overview.openTickets : 23} open helpdesk tickets need attention\n• Collection rate is strong at ${overview ? `${(overview.collectionRate ?? 0).toFixed(1)}%` : "94%"}\n\nCould you be more specific? You can ask me about dues, visitors, tickets, residents, maintenance, or reports.`,
    chips: ["Dues status", "Visitor activity", "Helpdesk tickets", "Monthly summary"],
  };
}

const QUICK_PROMPTS: QuickPrompt[] = [
  { icon: <CreditCard size={14} />, label: "Dues Status", prompt: "What is the current dues collection status?", color: "#10B981" },
  { icon: <DoorOpen size={14} />, label: "Visitor Activity", prompt: "Show me today's visitor activity and any alerts", color: "#6D28D9" },
  { icon: <Ticket size={14} />, label: "Open Tickets", prompt: "Summarize the open helpdesk tickets", color: "#F59E0B" },
  { icon: <Users size={14} />, label: "Residents", prompt: "Give me a resident and occupancy overview", color: "#1A6CF6" },
  { icon: <Wrench size={14} />, label: "Maintenance", prompt: "Any predictive maintenance alerts?", color: "#EF4444" },
  { icon: <TrendingUp size={14} />, label: "Monthly Report", prompt: "Give me this month's analytics summary", color: "#8B5CF6" },
];

/* ── Message bubble ── */
const BotMessage: React.FC<{ msg: Message; onChip: (t: string) => void }> = ({ msg, onChip }) => {
  const lines = msg.text.split("\n").filter(Boolean);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #1A6CF6, #6D28D9)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
        <Bot size={15} style={{ color: "#fff" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ background: "var(--nx-surface-1)", border: "1px solid var(--nx-border)", borderRadius: "0 12px 12px 12px", padding: "12px 16px", fontSize: 13, color: "var(--nx-text-primary)", lineHeight: 1.6 }}>
          {lines.map((line, i) => {
            const parts = line.split(/(\*\*[^*]+\*\*)/g);
            return (
              <p key={i} style={{ margin: i === 0 ? 0 : "6px 0 0" }}>
                {parts.map((part, j) =>
                  part.startsWith("**") && part.endsWith("**") ? (
                    <strong key={j} style={{ color: "var(--nx-text-primary)", fontWeight: 600 }}>{part.slice(2, -2)}</strong>
                  ) : (
                    <span key={j}>{part}</span>
                  )
                )}
              </p>
            );
          })}
        </div>
        {msg.chips && msg.chips.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {msg.chips.map(chip => (
              <button key={chip} onClick={() => onChip(chip)} style={{ padding: "4px 10px", borderRadius: 99, fontSize: 11, fontWeight: 500, border: "1px solid rgba(26,108,246,0.3)", background: "rgba(26,108,246,0.06)", color: "var(--nx-blue)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, transition: "all 0.15s" }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(26,108,246,0.12)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(26,108,246,0.06)"; }}
              >
                {chip} <ChevronRight size={10} />
              </button>
            ))}
          </div>
        )}
        <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginTop: 6 }}>
          {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
};

const UserMessage: React.FC<{ msg: Message }> = ({ msg }) => (
  <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexDirection: "row-reverse" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--nx-surface-3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
      <User size={15} style={{ color: "var(--nx-text-muted)" }} />
    </div>
    <div style={{ maxWidth: "70%" }}>
      <div style={{ background: "linear-gradient(135deg, #1A6CF6, #3B82F6)", borderRadius: "12px 0 12px 12px", padding: "10px 14px", fontSize: 13, color: "#fff", lineHeight: 1.5 }}>
        {msg.text}
      </div>
      <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginTop: 4, textAlign: "right" }}>
        {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </div>
    </div>
  </div>
);

/* ── Typing indicator ── */
const TypingIndicator: React.FC = () => (
  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #1A6CF6, #6D28D9)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Bot size={15} style={{ color: "#fff" }} />
    </div>
    <div style={{ background: "var(--nx-surface-1)", border: "1px solid var(--nx-border)", borderRadius: "0 12px 12px 12px", padding: "14px 16px", display: "flex", alignItems: "center", gap: 4 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--nx-blue)", opacity: 0.6, animation: `nx-typing-dot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  </div>
);

/* ── Main page ── */
const NexaBotPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const societyName = user?.societyName ?? "Your Society";

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "bot",
      text: `Hello, ${user?.fullName?.split(" ")[0] ?? "there"}! I'm **NexaBot**, your AI-powered society assistant.\n\nI can answer questions about dues, visitors, helpdesk tickets, residents, maintenance alerts, and more — all in plain language.\n\nWhat would you like to know?`,
      timestamp: new Date(),
      chips: ["Dues status", "Visitor activity", "Open tickets", "Monthly summary"],
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (societyId) {
      analytics.overview(societyId).then(setOverview).catch(() => {});
    }
  }, [societyId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const send = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { id: Date.now().toString(), role: "user", text: text.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    const delay = 800 + Math.random() * 600;
    setTimeout(() => {
      const { text: responseText, chips } = generateResponse(text, overview, societyName);
      const botMsg: Message = { id: (Date.now() + 1).toString(), role: "bot", text: responseText, timestamp: new Date(), chips };
      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);
    }, delay);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const clearChat = () => {
    setMessages([{
      id: "welcome-reset",
      role: "bot",
      text: `Chat cleared. How can I help you with **${societyName}** today?`,
      timestamp: new Date(),
      chips: ["Dues status", "Visitor activity", "Open tickets", "Monthly summary"],
    }]);
  };

  // Fetch live counts for status bar
  const [visitorCount, setVisitorCount] = useState<number | null>(null);
  const [ticketCount, setTicketCount] = useState<number | null>(null);
  useEffect(() => {
    if (!societyId) return;
    visitorsApi.list(societyId).then(page => setVisitorCount(page.content.filter(x => x.entryTime && new Date(x.entryTime).toDateString() === new Date().toDateString()).length)).catch(() => {});
    helpdesk.list(societyId).then(t => setTicketCount(t.filter(x => x.status === "open" || x.status === "in-progress").length)).catch(() => {});
  }, [societyId]);

  return (
    <PageLayout>
      <style>{`
        @keyframes nx-typing-dot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: "linear-gradient(135deg, #1A6CF6, #6D28D9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Bot size={22} style={{ color: "#fff" }} />
          </div>
          <div>
            <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 2 }}>NexaBot</h1>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--nx-text-muted)" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10B981" }} />
              AI Assistant · {societyName}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={clearChat} className="nx-btn nx-btn-outline nx-btn-sm" style={{ gap: 5 }}>
            <RotateCcw size={13} /> Clear Chat
          </button>
        </div>
      </div>

      {/* Live context bar */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { icon: <CreditCard size={13} />, label: "Collection Rate", value: overview ? `${(overview.collectionRate ?? 0).toFixed(0)}%` : "—", color: "#10B981" },
          { icon: <DoorOpen size={13} />, label: "Visitors Today", value: visitorCount !== null ? String(visitorCount) : overview ? String(overview.visitorsToday) : "—", color: "#6D28D9" },
          { icon: <Ticket size={13} />, label: "Open Tickets", value: ticketCount !== null ? String(ticketCount) : overview ? String(overview.openTickets) : "—", color: "#F59E0B" },
          { icon: <AlertTriangle size={13} />, label: "Pending Approvals", value: overview ? String(overview.pendingVisitors) : "—", color: "#EF4444" },
        ].map(s => (
          <div key={s.label} className="nx-card" style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", color: s.color, flexShrink: 0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--nx-text-primary)", lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginTop: 2 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 16, alignItems: "start" }}>
        {/* Chat panel */}
        <div className="nx-card" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 360px)", minHeight: 460 }}>
          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px 0", display: "flex", flexDirection: "column", gap: 16 }}>
            {messages.map(msg =>
              msg.role === "bot"
                ? <BotMessage key={msg.id} msg={msg} onChip={send} />
                : <UserMessage key={msg.id} msg={msg} />
            )}
            {isTyping && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ padding: "16px 20px", borderTop: "1px solid var(--nx-border)" }}>
            <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8 }}>
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask NexaBot anything about your society…"
                disabled={isTyping}
                style={{ flex: 1, padding: "10px 14px", background: "var(--nx-surface-2)", border: "1px solid var(--nx-border)", borderRadius: 10, fontSize: 13, color: "var(--nx-text-primary)", outline: "none", fontFamily: "var(--font-body)" }}
              />
              <button
                type="submit"
                disabled={!input.trim() || isTyping}
                className="nx-btn nx-btn-primary"
                style={{ padding: "10px 16px", gap: 6, borderRadius: 10, flexShrink: 0 }}
              >
                <Send size={14} />
              </button>
            </form>
            <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginTop: 6, textAlign: "center" }}>
              NexaBot uses society data to generate insights · Results are AI-assisted, not a substitute for manual verification
            </div>
          </div>
        </div>

        {/* Quick prompts sidebar */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="nx-card" style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
              <Sparkles size={13} style={{ color: "var(--nx-blue)" }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--nx-text-secondary)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Quick Prompts</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {QUICK_PROMPTS.map(qp => (
                <button
                  key={qp.label}
                  onClick={() => send(qp.prompt)}
                  disabled={isTyping}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 9, border: "1px solid var(--nx-border)", background: "transparent", cursor: "pointer", textAlign: "left", transition: "all 0.15s", width: "100%" }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = `${qp.color}08`; (e.currentTarget as HTMLButtonElement).style.borderColor = `${qp.color}40`; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--nx-border)"; }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: `${qp.color}15`, display: "flex", alignItems: "center", justifyContent: "center", color: qp.color, flexShrink: 0 }}>
                    {qp.icon}
                  </div>
                  <span style={{ fontSize: 12, color: "var(--nx-text-secondary)", fontWeight: 500 }}>{qp.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="nx-card" style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
              <Zap size={13} style={{ color: "#F59E0B" }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--nx-text-secondary)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Capabilities</span>
            </div>
            {[
              "Natural language queries",
              "Dues & billing analysis",
              "Visitor pattern insights",
              "Predictive maintenance",
              "Ticket prioritization",
              "Monthly AI forecasts",
            ].map(cap => (
              <div key={cap} style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 0", fontSize: 11, color: "var(--nx-text-muted)" }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--nx-blue)", flexShrink: 0 }} />
                {cap}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default NexaBotPage;
